import { FC, useEffect, useRef, useState } from 'react';
import { Dropdown, IconButton, Menu, MenuButton } from '@mui/joy';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import { useNotifications } from '@consolidocs-ui/utils';
import { NotificationPanel } from './NotificationPanel';
import './NotificationCenter.scss';

/**
 * Header bell that surfaces the notification history (e.g. upload errors).
 * Fully self-contained: it reads and mutates state through `useNotifications`,
 * so no props need to be threaded down from the header.
 */
export const NotificationCenter: FC = () => {
  const { notifications, unreadCount, markAllRead, remove, clearAll } =
    useNotifications();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    // Opening the panel counts as seeing the notifications.
    if (isOpen && unreadCount > 0) markAllRead();
  };

  // Close when clicking anywhere outside the bell + panel.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="notification-center">
      <Dropdown open={open} onOpenChange={(_, isOpen) => handleOpenChange(isOpen)}>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{
            root: {
              size: 'sm',
              variant: 'solid',
              color: 'neutral',
              className: 'notification-bell',
            },
          }}
        >
          <NotificationsActiveRoundedIcon />
          {unreadCount > 0 && (
            <span className="notification-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </MenuButton>

        <Menu
          placement="bottom-end"
          className="notification-menu"
          disablePortal
        >
          <NotificationPanel
            notifications={notifications}
            onDismiss={remove}
            onClearAll={clearAll}
          />
        </Menu>
      </Dropdown>
    </div>
  );
};
